import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ReactReduxContext, useDispatch, useSelector } from 'react-redux';

import { useMount } from '@provider-portal/common/hooks/useMount';

import { HttpErrorUtils } from '@bolteu/http-client/src/errors/HttpErrorUtils';
import {
  HttpResponseCodeBoltServer,
  HttpResponseErrorBoltServer,
} from '@bolteu/http-normalizer-bolt-server';
import { NetworkRequestState } from '@bolteu/redux-session';
import { AuthType, InitProps, SupportWidget, UnreadCase } from '@bolteu/support-widget';

import { useSelectedProviderId } from 'hooks/useSelectedProvider';
import { selectAccountStage } from 'rdx/account';
import { selectSessionAccessTokenState } from 'rdx/session/selectors';
import {
  getMainEntryWidgetUrlAsyncAction,
  getSupportCaseWidgetUrlAsyncAction,
  getSupportWidgetMqttConnectionInfoAsyncAction,
  getUnreadCasesWithLastMessageAsyncAction,
  resetSelectedSupportCaseAction,
  resetSupportWidgetDataAction,
  selectFetchingMainEntryWidgetUrlState,
  selectFetchingMqttConnectionInfo,
  selectFetchingSupportCaseUrlState,
  selectFetchingUnreadCasesState,
  selectMqttConnectionInfoState,
  selectSupportCaseUrlState,
  selectSupportMainEntryWidgetUrlState,
  selectSupportSelectedCaseState,
  selectUnreadCasesState,
} from 'rdx/support';
import { consoleLog } from 'utils/consoleLog';

import { useCommonAppServices } from '@provider-portal/common/services/ContextProviders';
import { ErrorReporter } from '@provider-portal/common/utils/errorReporting';

enum SupportWidgetMixpanelEventType {
  SUPPORT_WIDGET_CLICK = 'support_widget_click',
  SUPPORT_WIDGET_EMAIL_REDIRECT = 'support_widget_email_redirect',
}

const isRefreshTokenInvalidError = (error: unknown): boolean =>
  HttpErrorUtils.isInstanceOf(error, HttpResponseErrorBoltServer.ServerResponseError) &&
  error.response.code === HttpResponseCodeBoltServer.REFRESH_TOKEN_INVALID;

export const useCustomerSupport = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { mixpanelService, sessionService } = useCommonAppServices();
  const [isSupportWidgetInitialised, setIsSupportWidgetInitialised] = useState(false);
  const [isDataReset, setIsDataReset] = useState(false);

  const accessToken = useSelector(selectSessionAccessTokenState);
  const updatePromise = useRef<Promise<string> | null>(null);
  const mainEntryWidgetUrl = useSelector(selectSupportMainEntryWidgetUrlState);

  const supportCaseUrl = useSelector(selectSupportCaseUrlState);
  const unreadCases = useSelector(selectUnreadCasesState);
  const mqttConnectionInfo = useSelector(selectMqttConnectionInfoState);

  const selectedProviderId = useSelectedProviderId();
  const selectedCase = useSelector(selectSupportSelectedCaseState);
  const accountStage = useSelector(selectAccountStage);

  const fetchingMainEntryWidgetUrlState = useSelector(selectFetchingMainEntryWidgetUrlState);
  const fetchingSupportCaseUrlState = useSelector(selectFetchingSupportCaseUrlState);
  const fetchingUnreadCasesState = useSelector(selectFetchingUnreadCasesState);
  const fetchingMqttConnectionInfo = useSelector(selectFetchingMqttConnectionInfo);

  const [fetchUnreadCasesPromiseHandlers, setFetchUnreadCasesPromiseHandlers] = useState(null);
  const fetchUnreadCasesPromise = useCallback(() => {
    return new Promise<UnreadCase[]>((resolve, reject) => {
      dispatch(getUnreadCasesWithLastMessageAsyncAction.request());
      setFetchUnreadCasesPromiseHandlers({ resolve, reject });
    });
  }, [dispatch]);

  const { store } = useContext(ReactReduxContext);
  const isTokenExpired = (token: string) => {
    const tokenPayload = JSON.parse(atob(token.split('.')[1]));
    const expiryTime = tokenPayload.exp * 1000;
    return expiryTime < Date.now();
  };

  const reqTokenCallback = useCallback(async (): Promise<string> => {
    const currentToken = selectSessionAccessTokenState(store.getState());
    if (currentToken && !isTokenExpired(currentToken)) {
      return currentToken;
    }

    if (!updatePromise.current) {
      updatePromise.current = sessionService
        .updateAccessToken()
        .then((response) => response.access_token)
        .catch((error: unknown) => {
          // An invalid refresh token is expected (e.g. the user signed in on another device);
          // the main app's HTTP client drives the redirect-to-login, so swallow it. Report
          // anything unexpected — but still resolve '' so MQTT auth fails gracefully rather than
          // crashing the Portal via the global unhandled-rejection handler.
          if (!isRefreshTokenInvalidError(error)) {
            ErrorReporter.captureException(error);
          }
          return '';
        })
        .finally(() => {
          updatePromise.current = null;
        });
    }

    return updatePromise.current;
  }, [sessionService, store]);

  const supportWidget = useMemo(() => new SupportWidget(), []);

  const getSelectedCaseId = useCallback(() => {
    if (selectedCase && selectedCase.provider_id === selectedProviderId) {
      return selectedCase.case_id;
    }
    return undefined;
  }, [selectedCase, selectedProviderId]);

  const toggleCallback = useCallback(() => {
    if (selectedProviderId) {
      mixpanelService.trackProviderEvent(SupportWidgetMixpanelEventType.SUPPORT_WIDGET_CLICK, {
        providerId: selectedProviderId,
        lifecycleStatus: accountStage,
      });
    }
  }, [accountStage, mixpanelService, selectedProviderId]);

  const clearSelectedCase = useCallback(() => {
    if (selectedCase) {
      if (selectedProviderId && getSelectedCaseId()) {
        mixpanelService.trackProviderEvent(
          SupportWidgetMixpanelEventType.SUPPORT_WIDGET_EMAIL_REDIRECT,
          {
            providerId: selectedProviderId,
            lifecycleStatus: accountStage,
          },
        );
      }

      dispatch(resetSelectedSupportCaseAction());
    }
  }, [
    accountStage,
    dispatch,
    getSelectedCaseId,
    mixpanelService,
    selectedCase,
    selectedProviderId,
  ]);

  const getInitProps = useCallback((): InitProps => {
    return {
      url: mainEntryWidgetUrl,
      auth: { type: AuthType.JWT, requestToken: reqTokenCallback },
      locale: intl.locale,
      supportCaseUrl,
      unreadCases,
      getUnreadCases: fetchUnreadCasesPromise,
      mqttConnectionInfo,
      styleProps: { alwaysShowFloatingButton: true },
      selectedCaseId: getSelectedCaseId(),
      toggleCallback,
    };
  }, [
    fetchUnreadCasesPromise,
    getSelectedCaseId,
    intl.locale,
    mainEntryWidgetUrl,
    mqttConnectionInfo,
    reqTokenCallback,
    supportCaseUrl,
    toggleCallback,
    unreadCases,
  ]);

  const isDataInitialized = useCallback((): boolean => {
    return (
      fetchingMainEntryWidgetUrlState === NetworkRequestState.success &&
      fetchingSupportCaseUrlState === NetworkRequestState.success &&
      fetchingUnreadCasesState === NetworkRequestState.success &&
      fetchingMqttConnectionInfo === NetworkRequestState.success &&
      mainEntryWidgetUrl !== '' &&
      supportCaseUrl !== '' &&
      mqttConnectionInfo.broker_url !== '' &&
      mqttConnectionInfo.user_id !== 0 &&
      accessToken !== ''
    );
  }, [
    accessToken,
    fetchingMainEntryWidgetUrlState,
    fetchingMqttConnectionInfo,
    fetchingSupportCaseUrlState,
    fetchingUnreadCasesState,
    mainEntryWidgetUrl,
    mqttConnectionInfo.broker_url,
    mqttConnectionInfo.user_id,
    supportCaseUrl,
  ]);

  useEffect(() => {
    if (fetchUnreadCasesPromiseHandlers) {
      if (fetchingUnreadCasesState === NetworkRequestState.success) {
        fetchUnreadCasesPromiseHandlers.resolve(unreadCases);
        setFetchUnreadCasesPromiseHandlers(null);
      } else if (fetchingUnreadCasesState === NetworkRequestState.failure) {
        fetchUnreadCasesPromiseHandlers.reject(new Error('Error fetching unread cases data'));
        setFetchUnreadCasesPromiseHandlers(null);
      }
    }
  }, [
    fetchUnreadCasesPromise,
    unreadCases,
    fetchingUnreadCasesState,
    fetchUnreadCasesPromiseHandlers,
  ]);

  // Trigger data initialization
  useMount(() => {
    dispatch(resetSupportWidgetDataAction());
    setIsDataReset(true);

    dispatch(getMainEntryWidgetUrlAsyncAction.request());
    dispatch(getSupportCaseWidgetUrlAsyncAction.request());
    dispatch(getUnreadCasesWithLastMessageAsyncAction.request());
    dispatch(getSupportWidgetMqttConnectionInfoAsyncAction.request());
  });

  // SupportWidget initialisation
  useEffect(() => {
    if (!isSupportWidgetInitialised && isDataInitialized() && isDataReset) {
      try {
        supportWidget.init(getInitProps());
        setIsSupportWidgetInitialised(true);
        clearSelectedCase();
      } catch (error: unknown) {
        consoleLog('Failed initialising SupportWidget', error);
      }
    }
  }, [
    clearSelectedCase,
    dispatch,
    getInitProps,
    isDataInitialized,
    isDataReset,
    isSupportWidgetInitialised,
    mainEntryWidgetUrl,
    supportWidget,
  ]);

  // SupportWidget un-initialisation
  useEffect(() => {
    return () => {
      if (isSupportWidgetInitialised) {
        supportWidget.destroy().catch((e) => consoleLog('Failed destroying support widget', e));
      }
    };
  }, [isSupportWidgetInitialised, supportWidget]);
};
